import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import type { Location } from "react-router-dom";
import { Activities } from "./components/landing/Activities";
import { CTA } from "./components/landing/CTA";
import { Footer } from "./components/landing/Footer";
import { FourVedas } from "./components/landing/FourVedas";
import { Hero } from "./components/landing/Hero";
import { HouseOfUnity } from "./components/landing/HouseOfUnity";
import { Marquee } from "./components/landing/Marquee";
import { Mission } from "./components/landing/Mission";
import { SiteHeader } from "./components/platform/PlatformChrome";
import { PlateBreak } from "./components/landing/PlateBreak";
import { Practice } from "./components/landing/Practice";
import { Stats } from "./components/landing/Stats";
import { Vedas } from "./components/landing/Vedas";
import { BhagavadGita } from "./components/landing/BhagavadGita";
import { LandingPreviews } from "./components/landing/LandingPreviews";
import { YouTubeFeature } from "./components/landing/YouTubeFeature";
import { ScrollToHash } from "./components/ScrollToHash";
import { RequireAuth } from "./components/auth/RequireAuth";
import { ConnectionBanner } from "./components/platform/ConnectionBanner";
import { useOpenedFiles } from "./lib/useOpenedFiles";
import { isAppShell } from "./lib/platform";
import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import ForgotPasswordPage from "./pages/ForgotPassword";
import LinkDevicePage from "./pages/LinkDevice";
import ResetPasswordPage from "./pages/ResetPassword";
import LogoutPage from "./pages/Logout";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import DashboardProfile from "./pages/dashboard/DashboardProfile";
import DashboardCalendar from "./pages/dashboard/DashboardCalendar";
import EventForm from "./pages/dashboard/EventForm";
import ArticlesList from "./pages/dashboard/ArticlesList";
import ArticleForm from "./pages/dashboard/ArticleForm";
import DashboardEditor from "./pages/dashboard/Editor";
import YouTubeLayout from "./pages/dashboard/youtube/YouTubeLayout";
import YouTubeVideos from "./pages/dashboard/youtube/YouTubeVideos";
import YouTubeUpload from "./pages/dashboard/youtube/Upload";
import YouTubeEdit from "./pages/dashboard/youtube/EditVideo";
import YouTubeSeries from "./pages/dashboard/youtube/Series";
import YouTubeSeriesEditor from "./pages/dashboard/youtube/SeriesEditor";
import YouTubeAdvanced from "./pages/dashboard/youtube/Advanced";
import LibraryLayout from "./pages/dashboard/library/LibraryLayout";
import LibraryFeatured from "./pages/dashboard/library/LibraryFeatured";
import LibraryDocuments from "./pages/dashboard/library/LibraryDocuments";
import LibraryVideos from "./pages/dashboard/library/LibraryVideos";
import LibraryResources from "./pages/dashboard/library/LibraryResources";
import LibraryIntroduction from "./pages/dashboard/library/LibraryIntroduction";
import DocumentsList from "./pages/dashboard/DocumentsList";
import DocumentForm from "./pages/dashboard/DocumentForm";
import PublicNews from "./pages/PublicNews";
import PublicCalendar from "./pages/PublicCalendar";
import Sankalpa from "./pages/Sankalpa";
import AppDownload from "./pages/AppDownload";
// Code-split: the astronomical chart bundles astronomy-engine, so it loads only
// when /panchang is visited (kept out of the main bundle).
const Panchang = lazy(() => import("./pages/Panchang"));
// Gate D1 — the cross-WebView rendering check (specs/chant-editor 05 §4.1).
// Code-split: it is a diagnostic, run by hand, and no reader ever loads it.
const RenderCheck = lazy(() => import("./pages/RenderCheck"));
// The reading guide carries its own ~70 KB table of marked examples — its own chunk.
const PublicGuide = lazy(() => import("./pages/PublicGuide"));
const PurushaSuktamTest = lazy(() => import("./pages/tests/PurushaSuktam"));
const DurgaSuktamTest = lazy(() => import("./pages/tests/DurgaSuktam"));
const VishnuSuktamTest = lazy(() => import("./pages/tests/VishnuSuktam"));
const ShivaSankalpaSuktamTest = lazy(() => import("./pages/tests/ShivaSankalpaSuktam"));
const GanapatiAtharvashirshamTest = lazy(() => import("./pages/tests/GanapatiAtharvashirsham"));
const MantraPushpamTest = lazy(() => import("./pages/tests/MantraPushpam"));
const PujaVidhiTest = lazy(() => import("./pages/tests/PujaVidhi"));
const SamanaSuktamTest = lazy(() => import("./pages/tests/SamanaSuktam"));
// The chant editor is heavy (it pulls in the marking engine and the reader), so
// it is code-split and only loaded for someone who actually authors.
const ChantsList = lazy(() => import("./pages/dashboard/chants/ChantsList"));
const ChantEditor = lazy(() => import("./pages/dashboard/chants/ChantEditor"));
import PublicLibrary from "./pages/PublicLibrary";
import PublicYoutube from "./pages/PublicYoutube";
import PublicCourses from "./pages/PublicCourses";
import { EventPage } from "./components/event/EventPage";
import { EventModalRoute } from "./components/event/EventModalRoute";
import { ArticlePage } from "./components/article/ArticlePage";
import { DocumentPage } from "./components/document/DocumentPage";
import CoursePage from "./components/course/CoursePage";
import LessonPlayer from "./components/course/LessonPlayer";
import QuizPlayer from "./components/course/QuizPlayer";

/**
 * Inside the packaged app / installed PWA there is no marketing site: `/` is
 * rewritten to `/dashboard` at boot, and the header only ever shows platform
 * tabs. The PUBLIC INDEX routes duplicate a dashboard surface, so in the shell
 * they redirect to it — otherwise a deep link or a stray history entry drops
 * the user onto the public listing, complete with the marketing footer, inside
 * what is supposed to be the app.
 *
 * Detail routes are deliberately NOT redirected: `/library/:slug`,
 * `/news/:slug` and `/calendar/:slug` are the content itself, and the app has
 * to be able to open them.
 */
function shellOr(publicEl: React.ReactNode, dashboardPath: string) {
  return isAppShell ? <Navigate to={dashboardPath} replace /> : publicEl;
}

/** `shellOr` for a route with a parameter: a guide opened in the app shell
 *  goes to its dashboard twin, with its section anchor kept. */
function GuideRedirectOr() {
  const { slug } = useParams();
  const { hash } = useLocation();
  if (isAppShell) return <Navigate to={`/dashboard/library/introduction/${slug ?? ""}${hash}`} replace />;
  return (
    <Suspense fallback={<div className="min-h-[100svh]" aria-busy="true" />}>
      <PublicGuide surface="public" />
    </Suspense>
  );
}

function Landing() {
  return (
    <>
      <a
        href="#mission"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:bg-[var(--color-vellum)] focus:px-4 focus:py-2 focus:font-grotesk focus:text-[0.8rem] focus:tracking-[0.16em] focus:uppercase focus:border focus:border-[var(--color-ink)]"
      >
        Skip to content
      </a>
      <SiteHeader />
      <main>
        <Hero />
        <Marquee />
        <LandingPreviews />
        <Stats />
        <Mission />
        <Activities />
        <PlateBreak
          src="/photos/gathering-seated.jpg"
          alt="A large gathering of practitioners seated together, chanting during a yajña"
          plateNumber="II"
          caption="Chanting during a yajña"
          quote={{
            deva: "सत्यं वद। धर्मं चर।",
            iast: "Satyaṃ vada. Dharmaṃ chara.",
            english: "Speak the truth. Walk the path of dharma.",
            source: "Taittirīya Upaniṣad 1.11.1 — Śikṣāvallī",
          }}
        />
        <Vedas />
        <FourVedas />
        <BhagavadGita />
        <PlateBreak
          src="/photos/fire-ceremony.jpg"
          alt="An elder priest at a fire ceremony, hands offering grains into the flame"
          plateNumber="III"
          caption="The opening of the Ṛgveda"
          quote={{
            deva: "अग्निमीळे पुरोहितम्",
            iast: "Agnim īḷe purohitam",
            english: "I sing of Agni — the priest of the offering, the bearer of the gift.",
            source: "Ṛgveda 1.1.1 — the first verse of the Vedas",
          }}
        />
        <Practice />
        <YouTubeFeature />
        <HouseOfUnity />
        <CTA />
      </main>
      <Footer />
    </>
  );
}

/**
 * What is happening to a file the OS handed us.
 *
 * Above every route, like the connection banner and for the same reason:
 * opening a document is a state of the APP, not of the page that happens to be
 * on screen when the file arrives.
 */
function OpenedFileBanner({
  busy, error, dismiss,
}: { busy: string | null; error: string | null; dismiss: () => void }) {
  if (busy === null && error === null) return null;
  return (
    <div className="opened-file" role="status" aria-live="polite">
      {busy !== null ? (
        <span>Opening <strong>{busy}</strong>…</span>
      ) : (
        <>
          <span>{error}</span>
          <button type="button" onClick={dismiss} aria-label="Dismiss">✕</button>
        </>
      )}
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const background = (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation;
  // A file the OS handed to the desktop shell — a double-clicked `.vuchant`, or
  // a second launch handing its arguments to this window. A no-op in a browser.
  const opened = useOpenedFiles();

  return (
    <>
      <ScrollToHash />
      {/* Global, above every route: losing signal is a state of the app, not of
          the page you happen to be on. */}
      <ConnectionBanner />
      <OpenedFileBanner {...opened} />
      <Routes location={background ?? location}>
        {/*
          The packaged app and an installed PWA have no marketing site: "/" is
          the platform. Redirecting rather than rendering <Landing /> also keeps
          the app build honest, since vite.config.ts strips the landing-only
          photography from it (~22 MB that would otherwise ship in the APK to be
          shown as broken images).
        */}
        <Route
          path="/"
          element={isAppShell ? <Navigate to="/dashboard" replace /> : <Landing />}
        />

        {/* Public reading surfaces */}
        <Route path="/news" element={<PublicNews />} />
        <Route path="/news/:slug" element={<ArticlePage surface="public" />} />
        <Route path="/calendar" element={shellOr(<PublicCalendar />, "/dashboard/calendar")} />
        <Route path="/sankalpa" element={<Sankalpa />} />
        {/* Where you get the app. A "secret" route like /sankalpa — a link you
            hand to someone, not something the nav advertises. */}
        <Route path="/app" element={<AppDownload />} />
        <Route
          path="/panchang"
          element={
            <Suspense fallback={<div className="pa-page" aria-busy="true" />}>
              <Panchang />
            </Suspense>
          }
        />
        {/* Gate D1: the same marked fragment through the same renderer, in four
            scripts, measured. Run it in a browser and inside the desktop shell
            on every OS we ship to, and compare. A "secret" route — reachable,
            never linked. */}
        <Route
          path="/render-check"
          element={
            <Suspense fallback={<div aria-busy="true" />}>
              <RenderCheck />
            </Suspense>
          }
        />
        <Route path="/calendar/:slug" element={<EventPage surface="public" />} />

        {/* Chant reader — test template for the source-of-truth format */}
        <Route
          path="/tests/purusha-suktam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <PurushaSuktamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/durga-suktam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <DurgaSuktamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/vishnu-suktam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <VishnuSuktamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/shiva-sankalpa-suktam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <ShivaSankalpaSuktamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/ganapati-atharvashirsham"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <GanapatiAtharvashirshamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/mantra-pushpam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <MantraPushpamTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/puja-vidhi"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <PujaVidhiTest />
            </Suspense>
          }
        />
        <Route
          path="/tests/samana-suktam"
          element={
            <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--color-vellum)" }} aria-busy="true" />}>
              <SamanaSuktamTest />
            </Suspense>
          }
        />
        <Route path="/youtube" element={shellOr(<PublicYoutube />, "/dashboard/library/videos")} />
        <Route path="/library" element={shellOr(<PublicLibrary />, "/dashboard/library")} />
        <Route path="/library/documents" element={shellOr(<PublicLibrary tab="documents" />, "/dashboard/library/documents")} />
        <Route path="/library/videos" element={shellOr(<PublicLibrary tab="videos" />, "/dashboard/library/videos")} />
        <Route path="/library/series" element={shellOr(<PublicLibrary tab="series" />, "/dashboard/library/series")} />
        <Route path="/library/resources" element={shellOr(<PublicLibrary tab="resources" />, "/dashboard/library/resources")} />
        <Route path="/library/introduction" element={shellOr(<PublicLibrary tab="introduction" />, "/dashboard/library/introduction")} />
        <Route path="/library/introduction/:slug" element={<GuideRedirectOr />} />
        <Route path="/library/:slug" element={<DocumentPage surface="public" />} />

        {/* Courses (learner surface — login required) */}
        <Route element={<RequireAuth />}>
          <Route path="/courses" element={<PublicCourses />} />
          <Route path="/courses/:slug" element={<CoursePage surface="public" />} />
          <Route path="/courses/:slug/lessons/:lessonId" element={<LessonPlayer surface="public" />} />
          <Route path="/courses/:slug/quizzes/:quizId" element={<QuizPlayer surface="public" />} />
        </Route>

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* Where a desktop or terminal program sends its user to be approved.
            Readable signed out — the person has to see WHAT is asking before
            they are asked to log in — but only a signed-in person can allow. */}
        <Route path="/link" element={<LinkDevicePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/logout" element={<LogoutPage />} />

        {/* Authenticated dashboard */}
        <Route element={<RequireAuth />}>
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="profile" element={<DashboardProfile />} />

            <Route path="calendar" element={<DashboardCalendar />} />
            <Route path="calendar/new" element={<EventForm mode="create" />} />
            <Route path="calendar/:slug" element={<EventPage surface="dashboard" />} />
            <Route path="calendar/:slug/edit" element={<EventForm mode="edit" />} />

            <Route path="editor" element={<DashboardEditor />} />
            <Route path="articles" element={<ArticlesList />} />
            <Route path="articles/new" element={<ArticleForm mode="create" />} />
            <Route path="articles/:slug" element={<ArticlePage surface="dashboard" />} />
            <Route path="articles/:slug/edit" element={<ArticleForm mode="edit" />} />

            <Route path="documents" element={<DocumentsList />} />
            <Route path="documents/new" element={<DocumentForm mode="create" />} />
            <Route path="documents/:slug" element={<DocumentPage surface="dashboard" />} />
            <Route path="documents/:slug/edit" element={<DocumentForm mode="edit" />} />

            {/* The marked TEXTS. Separate from `documents`, which are the
                Library pages that embed them (05 §3.2). */}
            <Route path="chants" element={<ChantsList />} />
            <Route path="chants/:slug" element={<ChantEditor />} />

            <Route path="library" element={<LibraryLayout />}>
              <Route index element={<LibraryFeatured />} />
              <Route path="documents" element={<LibraryDocuments />} />
              <Route path="videos" element={<LibraryVideos />} />
              {/* Folded into Videos; the path stays so old links resolve. */}
              <Route path="series" element={<Navigate to="/dashboard/library/videos" replace />} />
              <Route path="resources" element={<LibraryResources />} />
              <Route path="introduction" element={<LibraryIntroduction />} />
            </Route>
            {/* A guide has its own contents rail, so it sits outside the
                Library's tabbed header rather than under it. */}
            <Route
              path="library/introduction/:slug"
              element={
                <Suspense fallback={<div aria-busy="true" />}>
                  <PublicGuide surface="dashboard" />
                </Suspense>
              }
            />

            <Route path="youtube" element={<YouTubeLayout />}>
              <Route index element={<YouTubeVideos />} />
              <Route path="upload" element={<YouTubeUpload />} />
              <Route path="videos/:id" element={<YouTubeEdit />} />
              <Route path="series" element={<YouTubeSeries />} />
              <Route path="series/:id" element={<YouTubeSeriesEditor />} />
              <Route path="advanced" element={<YouTubeAdvanced />} />
            </Route>
          </Route>
        </Route>
      </Routes>

      {/* Modal overlay routes — only render when a backgroundLocation is in the
          state. The list page underneath stays mounted (via the routes above
          rendered against `background`), so scroll position and any local UI
          state are preserved while the modal is open. */}
      {background && (
        <Routes>
          <Route
            path="/calendar/:slug"
            element={<EventModalRoute basePath="/calendar" newsBasePath="/news" />}
          />
          <Route
            path="/dashboard/calendar/:slug"
            element={
              <EventModalRoute
                basePath="/dashboard/calendar"
                newsBasePath="/dashboard/articles"
              />
            }
          />
        </Routes>
      )}
    </>
  );
}
